import React, { useState } from "react";
import { metadataService } from "../../../../../services/api";

const BulkStandardizeTagsModal = ({
  isOpen,
  onClose,
  selectedTags,
  deasyApiKey,
  vdbProfileName,
  llmProfileName,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [standardizationMode, setStandardizationMode] =
    useState("deduplication"); // deduplication or semantic
  const [completedTags, setCompletedTags] = useState([]);
  const [failedTags, setFailedTags] = useState({});

  if (!isOpen) return null;

  const handleStandardizeTags = async () => {
    setIsLoading(true);
    const tagNames = selectedTags.map((tag) =>
      typeof tag === "string" ? tag : tag.name,
    );
    const isSemantic = standardizationMode === "semantic";
    const onlyDedupe = standardizationMode === "deduplication";

    try {
      const response = await metadataService.standardizeMetadataBulk(
        vdbProfileName,
        tagNames,
        deasyApiKey,
        isSemantic ? llmProfileName : null,
        onlyDedupe,
      );

      if (response.data.success) {
        setCompletedTags(response.data.standardized_tags);
      } else {
        setCompletedTags(response.data.standardized_tags);
        setFailedTags(response.data.failed_tags);
      }
    } catch (error) {
      console.error("Error standardizing tags:", error);
      setFailedTags(
        Object.fromEntries(tagNames.map((tag) => [tag, "Request failed"])),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const ModalHeader = () => (
    <div className="bg-primary/5 px-6 py-4 border-b border-primary/10">
      <div className="flex items-center gap-2 text-gray-800">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5 text-primary"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
          />
        </svg>
        <span className="text-lg font-medium">Standardize Tag Values</span>
      </div>
    </div>
  );

  const LoadingState = () => (
    <div className="flex flex-col items-center justify-center py-12 space-y-4">
      <div className="w-16 h-16 relative">
        <div className="absolute inset-0 rounded-full border-t-2 border-primary animate-spin"></div>
        <div className="absolute inset-0 rounded-full border-2 border-gray-100"></div>
      </div>
      <div className="text-center space-y-2">
        <h3 className="text-lg font-medium text-gray-900">
          Standardizing tag values
        </h3>
        <p className="text-sm text-gray-500 max-w-sm">
          This may take a moment...
        </p>
      </div>
    </div>
  );

  const ResultsView = () => (
    <div className="p-6">
      {completedTags.length > 0 && (
        <div className="mb-4">
          <h3 className="text-md font-semibold text-gray-700 mb-2">
            Successfully standardized tags:
          </h3>
          <div className="bg-green-50 border border-green-200 rounded-md p-3">
            {completedTags.map((tag) => (
              <div key={tag} className="flex items-center gap-2 py-1">
                <svg
                  className="w-4 h-4 text-green-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <span className="text-sm text-gray-700">{tag}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {Object.keys(failedTags).length > 0 && (
        <div>
          <h3 className="text-md font-semibold text-gray-700 mb-2">
            Failed to standardize:
          </h3>
          <div className="bg-red-50 border border-red-200 rounded-md p-3">
            {Object.entries(failedTags).map(([tag, error]) => (
              <div key={tag} className="flex items-start gap-2 py-1">
                <svg
                  className="w-4 h-4 text-red-500 mt-1 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div>
                  <span className="text-sm font-medium text-gray-700">
                    {tag}
                  </span>
                  <p className="text-xs text-gray-500">{error}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
  const SelectionView = () => (
    <div className="p-6">
      <h3 className="text-md font-medium text-gray-700 mb-4">
        Choose standardization method:
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Deduplication Panel */}
        <div
          className={`border rounded-lg p-4 cursor-pointer transition-all ${
            standardizationMode === "deduplication"
              ? "border-primary bg-primary/5 ring-2 ring-primary/20"
              : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
          }`}
          onClick={() => setStandardizationMode("deduplication")}
        >
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-medium text-primary text-lg">Deduplication</h2>
            <div
              className={`w-4 h-4 rounded-full border ${
                standardizationMode === "deduplication"
                  ? "border-primary bg-primary"
                  : "border-gray-300"
              }`}
            >
              {standardizationMode === "deduplication" && (
                <svg
                  className="w-4 h-4 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              )}
            </div>
          </div>
          <p className="text-sm text-gray-600 text-left">
            Simple keyword-based deduplication that merges values with exact or
            close matches. This is faster but less sophisticated.
          </p>
        </div>

        {/* Semantic Clustering Panel */}
        <div
          className={`border rounded-lg p-4 cursor-pointer transition-all ${
            standardizationMode === "semantic"
              ? "border-primary bg-primary/5 ring-2 ring-primary/20"
              : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
          }`}
          onClick={() => setStandardizationMode("semantic")}
        >
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-medium text-primary text-lg">
              Semantic Clustering
            </h2>
            <div
              className={`w-4 h-4 rounded-full border ${
                standardizationMode === "semantic"
                  ? "border-primary bg-primary"
                  : "border-gray-300"
              }`}
            >
              {standardizationMode === "semantic" && (
                <svg
                  className="w-4 h-4 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              )}
            </div>
          </div>
          <p className="text-sm text-gray-600 text-left">
            Uses AI to identify values with similar meanings and group them into
            standardized categories. More powerful but may take longer.
          </p>
        </div>
      </div>

      {/* List of tags to standardize */}
      <div className="mt-8">
        <h3 className="text-md font-medium text-gray-700 mb-2">
          {" "}
          Tags for Standardization ({selectedTags.length})
        </h3>
        <div className="bg-gray-50 border border-gray-200 rounded-md p-3 max-h-40 overflow-y-auto">
          {selectedTags.map((tag) => (
            <div
              key={tag.tag_id || tag.name || tag}
              className="py-1 flex items-center gap-2"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 7h.01M7 3h5a1.99 1.99 0 0 1 1.414.586l7 7a2 2 0 0 1 0 2.828l-7 7a2 2 0 0 1-2.828 0l-7-7A1.99 1.99 0 0 1 3 12V7a4 4 0 0 1 4-4z"
                />
              </svg>
              <span className="text-sm text-gray-700">
                {typeof tag === "string" ? tag : tag.name}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const ModalFooter = () => (
    <div className="bg-gray-50 px-6 py-4 sm:flex sm:flex-row-reverse border-t border-gray-200">
      {!completedTags.length && !Object.keys(failedTags).length ? (
        <button
          type="button"
          onClick={handleStandardizeTags}
          disabled={isLoading}
          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-5 py-2 bg-primary text-base font-medium text-white hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:ml-4 sm:w-auto sm:text-sm transition duration-150"
        >
          Start Standardization
        </button>
      ) : (
        <button
          type="button"
          onClick={onClose}
          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-5 py-2 bg-primary text-base font-medium text-white hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:ml-4 sm:w-auto sm:text-sm transition duration-150"
        >
          Done
        </button>
      )}

      <button
        type="button"
        onClick={onClose}
        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-5 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:ml-4 sm:w-auto sm:text-sm transition duration-150"
      >
        Cancel
      </button>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-gray-500 bg-opacity-75 flex items-center justify-center">
      <div
        className="bg-white rounded-lg shadow-xl overflow-hidden w-full max-w-4xl transform transition-all mx-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-headline"
      >
        <ModalHeader />

        <div className="bg-white max-h-[70vh] overflow-y-auto">
          {isLoading ? (
            <LoadingState />
          ) : completedTags.length > 0 || Object.keys(failedTags).length > 0 ? (
            <ResultsView />
          ) : (
            <SelectionView />
          )}
        </div>

        <ModalFooter />
      </div>
    </div>
  );
};

export default BulkStandardizeTagsModal;
